import React, { useMemo, useState } from 'react'
import { hapticTick } from '../../../../../_utils/haptics'
import { SOCIAL_VISIBLE, AGENT_VISIBLE } from '../../../../../config/featureFlags'
import DateRangePicker from '../../../../finance/components/DateRangePicker'
import { ChevronDownIcon } from '../../../../finance/icons/FinanceIcons'
import { getDatePresetLabel } from '../../../../finance/data/mockData'
import { useViewingSites } from '../../../../multiLocation/viewingSites'
import useSites from '../../../../multiLocation/useSites'
import useWindowSize from '../../../../new-service/useWindowSize'
import { SampleDataBanner } from '../../../../marketing/campaigns/sampleCampaigns'
import { usePerformance } from '../usePerformance'
import { lastFourWeeks } from '../data/results'
import OverviewTab from './OverviewTab'
import CampaignsTab from './CampaignsTab'
import { SocialTab, JourneysTab, LoyaltyTab, FrontDeskTab } from './Tabs'
import { listOf } from './parts'
import { keepFrom } from '../../../../marketing/goUp'
import '../../../../marketing/campaigns/Campaigns.css'
import '../Performance.css'

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'campaigns', label: 'Campaigns' },
  ...(SOCIAL_VISIBLE ? [{ id: 'social', label: 'Social' }] : []),
  { id: 'journeys', label: 'Journeys and reminders' },
  { id: 'loyalty', label: 'Loyalty and vouchers' },
  ...(AGENT_VISIBLE ? [{ id: 'frontdesk', label: 'Front desk' }] : []),
]
// What can only count the location this device is on for now, by tab
const THIS_LOCATION_ONLY = {
  overview: listOf(['Gift vouchers', 'journeys', ...(AGENT_VISIBLE ? ['the front desk'] : []), ...(SOCIAL_VISIBLE ? ['posts'] : [])]),
  social: 'Social',
  journeys: 'Journeys',
  loyalty: 'Loyalty and vouchers',
  frontdesk: 'The front desk',
}

// Marketing › Performance: what marketing brought in, across campaigns,
// social, journeys, reminders, loyalty, vouchers and the front desk
const PerformanceHub = ({ dispatch, history, location, currency = '£' }) => {
  const [datePreset, setDatePreset] = useState('last4weeks')
  const [range, setRange] = useState(lastFourWeeks)
  const [pickerOpen, setPickerOpen] = useState(false)
  // The header Location picker's selection; undefined = this location only.
  const viewing = useViewingSites()
  const salonIds = viewing.spans ? viewing.salonIds : undefined
  const [demo, setDemo] = useState(false)
  const asked = new URLSearchParams((location && location.search) || '').get('tab')
  const tab = TABS.some(t => t.id === asked) ? asked : 'overview'
  const data = usePerformance({ dispatch, range, salonIds, demo, frontDesk: AGENT_VISIBLE })
  const here = String(localStorage.getItem('salonId') || '')
  const elsewhere = useMemo(() => (salonIds || []).some(id => String(id) !== here), [salonIds, here])
  const { active } = useSites()
  // At 920px and under the header stacks and the dates sit on the left, so
  // the calendar opens rightwards to stay on screen
  const { width } = useWindowSize()

  const pick = (id) => {
    if (id === tab) return
    hapticTick()
    history.replace(id === 'overview' ? '/marketing-performance' : `/marketing-performance?tab=${id}`, keepFrom(location))
  }
  const label = datePreset ? getDatePresetLabel(datePreset)
    : `${range.start ? range.start.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '…'} to ${range.end ? range.end.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '…'}`

  return (
    <div className="cmp-wrap pf">
      <div className="cmp-head">
        <div className="cmp-head-text">
          <h1 className="cmp-title">Performance</h1>
          <p className="cmp-subtitle">What your marketing brought in, and what did it.</p>
        </div>
        <div className="pf-head-actions">
          <div className="pf-dates">
            <button type="button" className="pf-date-btn" onClick={() => setPickerOpen(o => !o)} aria-expanded={pickerOpen}>
              {label}
              <ChevronDownIcon size={16} />
            </button>
            <DateRangePicker
              isOpen={pickerOpen}
              onClose={() => setPickerOpen(false)}
              startDate={range.start}
              endDate={range.end}
              selectedPreset={datePreset}
              onPresetChange={setDatePreset}
              align={width <= 920 ? 'left' : 'right'}
              onApply={(start, end) => setRange({ start, end })}
            />
          </div>
        </div>
      </div>

      <div className="pf-tabs" role="tablist" aria-label="Performance">
        {TABS.map(t => (
          <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} className={`pf-tab-btn${tab === t.id ? ' is-on' : ''}`} onClick={() => pick(t.id)}>{t.label}</button>
        ))}
      </div>

      {elsewhere && THIS_LOCATION_ONLY[tab] && (
        <p className="pf-where">{THIS_LOCATION_ONLY[tab]} can only show {(active && active.salonName) || 'this location'} for now. Emails, texts and campaigns show every location you picked.</p>
      )}
      {demo && <SampleDataBanner onExit={() => setDemo(false)} />}

      {tab === 'overview' && <OverviewTab data={data} currency={currency} history={history} frontDeskOn={AGENT_VISIBLE} demo={demo} />}
      {tab === 'campaigns' && <CampaignsTab data={data} history={history} demo={demo} onDemo={setDemo} />}
      {tab === 'social' && <SocialTab data={data} history={history} currency={currency} />}
      {tab === 'journeys' && <JourneysTab data={data} history={history} />}
      {tab === 'loyalty' && <LoyaltyTab data={data} history={history} currency={currency} />}
      {tab === 'frontdesk' && <FrontDeskTab data={data} history={history} />}
    </div>
  )
}

export default PerformanceHub
